import { useState } from 'react';
import type { ChangeEvent } from 'react';

import { ButtonGroup, IconButton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import UploadButton from '../../shared/UploadButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteAsset, insertAsset } from '../../../actions/chatAssets';
import getFile from '../../../util/getFile';
import PromptField from './PromptField';
import { useApi } from '../../../providers/ApiContext';
import type { EntityId } from '@/types';
import type { FileResult } from '@/types/fileTypes';

interface MultiModalPromptProps {
  chat_id: EntityId;
  prompt: string;
  image_id: EntityId | null;
  error: boolean | string;
  setPrompt: (value: string) => void;
  setImageId: (value: EntityId | null) => void;
  setImageData: (value: FileResult | null) => void;
  setError: (value: boolean | string) => void;
  onSubmit: () => void;
  generating: boolean;
  disabled: boolean;
  stopGeneration: () => void;
  [key: string]: unknown;
}

export default function MultiModalPrompt({
  chat_id,
  prompt,
  image_id,
  error,
  setPrompt,
  setImageId,
  setImageData,
  setError,
  onSubmit,
  generating,
  disabled,
  stopGeneration,
  ...rest
}: MultiModalPromptProps) {
  const api = useApi();
  const [image, setImage] = useState<FileResult | undefined>(undefined);
  const [prompt_error, setPromptError] = useState<boolean | string>(false);
  const [image_error, setImageError] = useState<boolean | string>(false);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !api) return;
    const image = await getFile(e.target.files[0]);
    const file_name = image.name;

    if (image.base64contents.length > 1024 ** 2 * 8) {
      setImageError('image too big');
      setError(prompt_error || image_error);
    } else {
      setImageError(false);
      setError(prompt_error || image_error);
    }
    const response = await insertAsset(
      api,
      chat_id,
      file_name,
      image.type,
      image.contents.buffer as ArrayBuffer,
    );

    setImageId(response.entity_id);
    setImage(image);
    setImageData(image);
  };

  const handleDeleteImage = async () => {
    if (!image_id || !api) return;
    await deleteAsset(api, chat_id, image_id);
    setImageId(null);
    setImage(undefined);
    setImageData(null);
    setImageError(false);
    setError(prompt_error || image_error);
  };

  const handlePromptError = (error: boolean | string) => {
    setPromptError(error);
    setError(prompt_error || image_error);
  };

  return (
    <Grid container spacing={0}>
      <Grid size={12}>
        <Typography variant='caption'>
          <b>Disclaimer:</b> The generated output is for assistance only and
          should be reviewed by subject matter experts before being used in any
          product or decision.
        </Typography>
      </Grid>
      <Grid size={12}>
        <ButtonGroup variant='outlined' color='secondary'>
          <UploadButton
            tooltip={'Image must be smaller than 1 MB'}
            {...rest}
            variant='outlined'
            onChange={handleImageChange}
            disabled={disabled}
          />
        </ButtonGroup>
      </Grid>
      {image ? (
        <Grid size={12}>
          <Stack direction='row' spacing={1} alignItems={'center'}>
            <img
              style={{
                width: '250px',
                height: '250px',
                maxWidth: '100%',
                maxHeight: '100%',
              }}
              alt='uploaded'
              src={image.base64contents}
            />
            <IconButton onClick={handleDeleteImage} color='secondary'>
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Grid>
      ) : undefined}
      {image_error ? (
        <Grid size={12}>
          <Typography color='red' variant='caption'>
            Image too large
          </Typography>
        </Grid>
      ) : undefined}
      <Grid size={12}>
        <PromptField
          {...rest}
          value={prompt}
          error={prompt_error}
          setValue={setPrompt}
          setError={handlePromptError}
          onSubmit={onSubmit}
          disabled={disabled}
          generating={generating}
          stopGeneration={stopGeneration}
          tooltip={
            disabled && !generating
              ? 'Please select a Document Types Filter before asking a question'
              : undefined
          }
        />
      </Grid>
    </Grid>
  );
}

